// Native WebMCP is experimental. Keep these local types until lib.dom ships it.
export interface BrowserTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations: { readOnlyHint: boolean; consequentialHint: boolean };
  execute: (
    input: unknown,
    options?: { signal?: AbortSignal },
  ) => Promise<unknown>;
}

interface ModelContext {
  registerTool: (
    tool: BrowserTool,
    options: { signal: AbortSignal },
  ) => Promise<void> | void;
  // Earlier navigator-based implementations have explicit unregistration.
  unregisterTool?: (name: string) => void;
}

type ToolHost = {
  modelContext?: ModelContext;
};

export const REFERENCE_PATHS = [
  "/",
  "/transparency",
  "/developers",
  "/pricing",
  "/methodology",
  "/privacy",
  "/terms",
  "/extension",
  "/supported-sites",
] as const;

const MODELS = ["stylome-1.2-large", "stylome-1.0", "stylome-fast-1.0"];

function argumentsFor(input: unknown, allowed: string[]) {
  if (
    !input ||
    typeof input !== "object" ||
    Array.isArray(input) ||
    Object.keys(input).some((key) => !allowed.includes(key))
  ) {
    throw new TypeError("Invalid tool arguments.");
  }
  return input as Record<string, unknown>;
}

/** Install only into a browser-provided context; never create a shim. */
export function registerBrowserTools({
  navigate,
  documentHost = document as Document & ToolHost,
  navigatorHost = navigator as Navigator & ToolHost,
  fetchPublic = fetch,
  onRegistrationError = () =>
    console.warn("Stylome browser tools could not be registered."),
}: {
  navigate: (path: string) => void;
  documentHost?: ToolHost;
  navigatorHost?: ToolHost;
  fetchPublic?: typeof fetch;
  onRegistrationError?: () => void;
}): () => void {
  const current = documentHost.modelContext;
  const legacy = current?.registerTool ? undefined : navigatorHost.modelContext;
  const context = current?.registerTool ? current : legacy;
  if (!context?.registerTool) return () => {};

  const lifetime = new AbortController();
  const legacyNames = new Set<string>();
  const signalFor = (signal?: AbortSignal) =>
    signal ? AbortSignal.any([lifetime.signal, signal]) : lifetime.signal;
  const readPublic = async (url: string, type: string, signal: AbortSignal) => {
    signal.throwIfAborted();
    const response = await fetchPublic(url, {
      method: "GET",
      credentials: "omit",
      redirect: "error",
      headers: { Accept: type },
      signal,
    });
    if (
      !response.ok ||
      response.headers
        .get("content-type")
        ?.split(";")[0]
        .trim()
        .toLowerCase() !== type
    ) {
      throw new Error("The public reference is unavailable. Try again later.");
    }
    return response;
  };

  const tools: BrowserTool[] = [
    {
      name: "stylome_get_benchmarks",
      description:
        "Read Stylome's published benchmark reports, tables, source references and scientific limitations. Optionally select a model. These are published measurements, not a new evaluation; compare models at matched false-positive rates on the same populations.",
      inputSchema: {
        type: "object",
        properties: { model: { type: "string", enum: MODELS } },
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true, consequentialHint: false },
      async execute(input, options) {
        const { model } = argumentsFor(input, ["model"]);
        if (model !== undefined && !MODELS.includes(model as string))
          throw new TypeError("Select a supported benchmark model.");
        const signal = signalFor(options?.signal);
        const response = await readPublic(
          "/benchmarks.json",
          "application/json",
          signal,
        );
        const feed = await response.json();
        signal.throwIfAborted();
        if (
          feed?.schema_version !== "stylome-published-benchmarks-v1" ||
          !Array.isArray(feed.interpretation) ||
          !Array.isArray(feed.models)
        )
          throw new Error(
            "The public benchmark feed has an unsupported format.",
          );
        const models = model
          ? feed.models.filter(
              (entry: { model: string }) => entry.model === model,
            )
          : feed.models;
        if (!models.length)
          throw new Error("The requested published benchmark is unavailable.");
        // Keep full report text, context, dates, status, and caveats with numbers.
        return { ...feed, models };
      },
    },
    {
      name: "stylome_get_api_guidance",
      description:
        "Read the public developer guide: model labels, relative-confidence limits, normalization and input lengths, pricing, authentication, retention, rate limits and retry rules. This does not score text, access an account or create a key.",
      inputSchema: {
        type: "object",
        properties: {},
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true, consequentialHint: false },
      async execute(input, options) {
        argumentsFor(input, []);
        const signal = signalFor(options?.signal);
        const response = await readPublic(
          "/developers",
          "text/markdown",
          signal,
        );
        const guidance = await response.text();
        signal.throwIfAborted();
        if (!guidance.trim())
          throw new Error("The public developer guide is unavailable.");
        return {
          source_url: "https://stylome.io/developers",
          openapi_url: "https://stylome.io/api/openapi.json",
          auth_url: "https://stylome.io/auth.md",
          content_type: "text/markdown",
          guidance,
        };
      },
    },
    {
      name: "stylome_open_reference",
      description:
        "Open a public Stylome reference page or the scanner for the user to review. Changes the visible page and may clear unsaved page input. Does not submit text, run scoring, change an account or share a receipt.",
      inputSchema: {
        type: "object",
        properties: { path: { type: "string", enum: REFERENCE_PATHS } },
        required: ["path"],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, consequentialHint: true },
      async execute(input, options) {
        const { path } = argumentsFor(input, ["path"]);
        if (!REFERENCE_PATHS.includes(path as (typeof REFERENCE_PATHS)[number]))
          throw new TypeError("Select a supported public reference path.");
        signalFor(options?.signal).throwIfAborted();
        navigate(path as string);
        return { path, url: `https://stylome.io${path}` };
      },
    },
  ];

  const stop = () => {
    if (lifetime.signal.aborted) return;
    lifetime.abort();
    // The current document API unregisters through the registration signal.
    // Old native navigator APIs may ignore that option and use this method.
    for (const name of legacyNames) context.unregisterTool?.(name);
    legacyNames.clear();
  };
  for (const tool of tools) {
    try {
      const registration = context.registerTool(tool, {
        signal: lifetime.signal,
      });
      if (legacy && registration === undefined) legacyNames.add(tool.name);
      // A rejected registration must not become an unhandled rejection or
      // interfere with ordinary browsing. Abort removes partial registrations.
      void Promise.resolve(registration).catch(() => {
        if (!lifetime.signal.aborted) {
          stop();
          onRegistrationError();
        }
      });
    } catch {
      stop();
      onRegistrationError();
      break;
    }
  }
  return stop;
}

/** Keep registration stable across SPA routes and refresh it after BFCache. */
export function startBrowserTools(navigate: (path: string) => void) {
  let stop = registerBrowserTools({ navigate });
  const hide = () => stop();
  const show = (event: PageTransitionEvent) => {
    if (event.persisted) stop = registerBrowserTools({ navigate });
  };
  window.addEventListener("pagehide", hide);
  window.addEventListener("pageshow", show);
  return () => {
    stop();
    window.removeEventListener("pagehide", hide);
    window.removeEventListener("pageshow", show);
  };
}
